import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All available permissions grouped by category
const PERMISSION_CATEGORIES = {
  'Employee Management': [
    { id: 'employees.view', label: 'View Employees' },
    { id: 'employees.create', label: 'Create Employees' },
    { id: 'employees.edit', label: 'Edit Employees' },
    { id: 'employees.delete', label: 'Delete Employees' },
  ],
  'Department Management': [
    { id: 'departments.view', label: 'View Departments' },
    { id: 'departments.create', label: 'Create Departments' },
    { id: 'departments.edit', label: 'Edit Departments' },
    { id: 'departments.delete', label: 'Delete Departments' },
  ],
  'Attendance': [
    { id: 'attendance.view_own', label: 'View Own Attendance' },
    { id: 'attendance.view_all', label: 'View All Attendance' },
    { id: 'attendance.checkin', label: 'Check In/Out' },
    { id: 'attendance.manage', label: 'Manage Attendance' },
  ],
  'Leave Management': [
    { id: 'leaves.view_own', label: 'View Own Leaves' },
    { id: 'leaves.view_all', label: 'View All Leaves' },
    { id: 'leaves.create', label: 'Apply for Leave' },
    { id: 'leaves.approve', label: 'Approve Leaves' },
    { id: 'leaves.reject', label: 'Reject Leaves' },
  ],
  'Payroll': [
    { id: 'payroll.view_own', label: 'View Own Payroll' },
    { id: 'payroll.view_all', label: 'View All Payroll' },
    { id: 'payroll.process', label: 'Process Payroll' },
    { id: 'payroll.approve', label: 'Approve Payroll' },
  ],
  'Performance': [
    { id: 'performance.view_own', label: 'View Own Performance' },
    { id: 'performance.view_all', label: 'View All Performance' },
    { id: 'performance.create', label: 'Create Reviews' },
    { id: 'performance.review', label: 'Conduct Reviews' },
  ],
  'Documents': [
    { id: 'documents.view_own', label: 'View Own Documents' },
    { id: 'documents.view_all', label: 'View All Documents' },
    { id: 'documents.upload', label: 'Upload Documents' },
    { id: 'documents.delete', label: 'Delete Documents' },
  ],
  'Announcements': [
    { id: 'announcements.view', label: 'View Announcements' },
    { id: 'announcements.create', label: 'Create Announcements' },
    { id: 'announcements.edit', label: 'Edit Announcements' },
    { id: 'announcements.delete', label: 'Delete Announcements' },
  ],
  'Assets': [
    { id: 'assets.view', label: 'View Assets' },
    { id: 'assets.create', label: 'Create Assets' },
    { id: 'assets.edit', label: 'Edit Assets' },
    { id: 'assets.delete', label: 'Delete Assets' },
    { id: 'assets.assign', label: 'Assign Assets' },
  ],
  'HR Policies': [
    { id: 'policies.view', label: 'View Policies' },
    { id: 'policies.create', label: 'Create Policies' },
    { id: 'policies.edit', label: 'Edit Policies' },
    { id: 'policies.delete', label: 'Delete Policies' },
    { id: 'policies.acknowledge', label: 'Acknowledge Policies' },
  ],
  'Travel & Expense': [
    { id: 'expenses.view_own', label: 'View Own Expenses' },
    { id: 'expenses.view_all', label: 'View All Expenses' },
    { id: 'expenses.create', label: 'Create Expenses' },
    { id: 'expenses.approve', label: 'Approve Expenses' },
    { id: 'expenses.reject', label: 'Reject Expenses' },
  ],
  'Legal & Compliance': [
    { id: 'legal.view', label: 'View Legal Module' },
    { id: 'legal.templates_manage', label: 'Manage Templates' },
    { id: 'legal.documents_create', label: 'Create Documents' },
    { id: 'legal.documents_approve', label: 'Approve Documents' },
    { id: 'legal.cases_view', label: 'View Cases' },
    { id: 'legal.cases_manage', label: 'Manage Cases' },
  ],
  'Reports': [
    { id: 'reports.view_basic', label: 'View Basic Reports' },
    { id: 'reports.view_advanced', label: 'View Advanced Reports' },
    { id: 'reports.export', label: 'Export Reports' },
  ],
  'System Administration': [
    { id: 'system.admin', label: 'System Admin' },
    { id: 'roles.manage', label: 'Manage Roles' },
    { id: 'users.manage', label: 'Manage Users' },
  ],
};

function RoleManagement({ user, onLogout }) {
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await axios.get(`${API}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setError(error.response?.data?.detail || 'Unable to load roles. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      if (selectedRole) {
        await axios.put(`${API}/roles/${selectedRole.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Role updated successfully!');
      } else {
        await axios.post(`${API}/roles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Role created successfully!');
      }
      resetForm();
      fetchRoles();
    } catch (error) {
      alert('Failed to save role: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (role) => {
    setSelectedRole(role);
    setFormData({
      role_name: role.role_name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowForm(true);
  };

  const handleDelete = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role "${roleName}"?`)) return;

    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      await axios.delete(`${API}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Role deleted successfully!');
      fetchRoles();
    } catch (error) {
      alert('Failed to delete role: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      role_name: '',
      description: '',
      permissions: []
    });
    setSelectedRole(null);
    setShowForm(false);
  };

  const openAddForm = () => {
    setFormData({
      role_name: '',
      description: '',
      permissions: []
    });
    setSelectedRole(null);
    setShowForm(true);
  };

  const seedDefaultRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      await axios.post(`${API}/roles/seed-defaults`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchRoles();
    } catch (seedError) {
      setError(seedError.response?.data?.detail || 'Unable to create default roles.');
      setLoading(false);
    }
  };

  const togglePermission = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId]
    }));
  };

  const toggleCategory = (category) => {
    const categoryPermissions = PERMISSION_CATEGORIES[category].map(p => p.id);
    const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));
    
    if (allSelected) {
      // Deselect all in category
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !categoryPermissions.includes(p))
      }));
    } else {
      // Select all in category
      const newPermissions = [...new Set([...formData.permissions, ...categoryPermissions])];
      setFormData(prev => ({
        ...prev,
        permissions: newPermissions
      }));
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Role Management</h1>
            <p className="text-gray-600 mt-1">Manage roles and permissions for your organization</p>
          </div>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button
              onClick={openAddForm}
              className="px-6 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: '#2b3380'}}
            >
              + Create Role
            </button>
          )}
        </div>

        {error && (
          <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between gap-4">
            <span>{error}</span>
            <button type="button" onClick={fetchRoles} className="font-semibold whitespace-nowrap">Retry</button>
          </div>
        )}

        {/* Role Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedRole ? 'Edit Role' : 'Create New Role'}
                  </h2>
                  <button onClick={resetForm} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Role Name *</label>
                      <input
                        type="text"
                        value={formData.role_name}
                        onChange={(e) => setFormData({...formData, role_name: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        required
                        disabled={selectedRole?.is_system_role}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                        rows="2"
                      />
                    </div>
                  </div>

                  {/* Permissions */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold mb-4" style={{color: '#2b3380'}}>Permissions</h3>
                    <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                      {Object.entries(PERMISSION_CATEGORIES).map(([category, permissions]) => {
                        const categoryPermissions = permissions.map(p => p.id);
                        const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));
                        const someSelected = categoryPermissions.some(p => formData.permissions.includes(p));

                        return (
                          <div key={category} className="mb-4 pb-4 border-b last:border-b-0">
                            <div className="flex items-center mb-2">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => toggleCategory(category)}
                                className="mr-2 w-4 h-4"
                                style={{
                                  accentColor: '#2b3380',
                                  opacity: someSelected && !allSelected ? 0.5 : 1
                                }}
                              />
                              <label className="font-semibold text-gray-800 cursor-pointer" onClick={() => toggleCategory(category)}>
                                {category}
                              </label>
                            </div>
                            <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                              {permissions.map(permission => (
                                <div key={permission.id} className="flex items-center">
                                  <input
                                    type="checkbox"
                                    id={permission.id}
                                    checked={formData.permissions.includes(permission.id)}
                                    onChange={() => togglePermission(permission.id)}
                                    className="mr-2 w-4 h-4"
                                    style={{accentColor: '#2b3380'}}
                                  />
                                  <label
                                    htmlFor={permission.id}
                                    className="text-sm text-gray-700 cursor-pointer"
                                  >
                                    {permission.label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      Selected: {formData.permissions.length} permissions
                    </div>
                  </div>

                  {selectedRole?.is_system_role && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-yellow-800">
                        ⚠️ This is a system role. Role name cannot be modified.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-3 justify-end">
                    <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      {selectedRole ? 'Update Role' : 'Create Role'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Roles List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">Loading roles...</p>
          </div>
        ) : roles.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center shadow-sm">
            <div className="text-4xl mb-3">🔐</div>
            <h2 className="text-xl font-bold text-gray-900">No roles configured</h2>
            <p className="text-gray-500 mt-2 mb-5">Create the standard ERP roles and then customize their permissions.</p>
            <button type="button" onClick={seedDefaultRoles} className="px-5 py-2.5 rounded-lg bg-[#2b3380] text-white font-medium">Create default roles</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map((role) => (
              <div key={role.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900">{role.role_name}</h3>
                    {role.is_system_role && (
                      <span className="inline-block mt-1 px-2 py-1 bg-[#2b338030] text-[#2b3380] text-xs rounded">
                        System Role
                      </span>
                    )}
                  </div>
                  <div className="text-2xl">🔐</div>
                </div>
                
                {role.description && (
                  <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                )}
                
                <div className="mb-4">
                  <div className="text-sm text-gray-700 mb-2">
                    <strong>Permissions:</strong> {role.permissions?.length || 0}
                  </div>
                  {role.permissions && role.permissions.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.slice(0, 3).map((perm, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {perm.split('.')[0]}
                        </span>
                      ))}
                      {role.permissions.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          +{role.permissions.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {(user.role === 'Admin' || user.role === 'HR Manager') && (
                  <div className="flex gap-2 mt-4 pt-4 border-t">
                    <button
                      onClick={() => handleEdit(role)}
                      className="flex-1 px-3 py-2 bg-[#2b3380] text-white rounded hover:bg-[#1f2660] text-sm"
                    >
                      Edit
                    </button>
                    {!role.is_system_role && user.role === 'Admin' && (
                      <button
                        onClick={() => handleDelete(role.id, role.role_name)}
                        className="flex-1 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default RoleManagement;
